import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import { useI18n, usePageTitle } from '../../i18n/index.jsx';
import { formatPrice } from '../../format.js';
import { resizeImage } from '../../images.js';
import { countryOptions } from '../../countries.js';
import { UNITS, INCOTERMS, MAX_SPECS, incotermLabel, countryName } from '../../productOptions.js';
import { useCategoryTree } from '../../categoryTree.js';
import { Alert, Field } from '../../components/ui.jsx';
import PhotoUploader from '../../components/PhotoUploader.jsx';
import RichTextEditor from '../../components/RichTextEditor.jsx';
import UpgradeCard, { NoPermissionCard } from '../../components/UpgradeCard.jsx';
import { allowed } from '../account/AccountLayout.jsx';
import CropModal from '../admin/content/CropModal.jsx';
import CategoryStep from './wizard/CategoryStep.jsx';
import SpecFields, { specsToBody, checkSpecs } from './wizard/SpecFields.jsx';
import './wizard/wizard.css';
import TutorialSlot from '../../tutorials/TutorialSlot.jsx';

// Add / edit a product in 4 steps: category (photo tiles + search),
// photos & name, details (from the category's spec template), price &
// delivery. Back / "Save draft" / Next are on every step; drafts also save
// themselves every 30 s.
// - A draft is private (status 'draft'): fields, photos and brochure are
//   saved, nothing is reviewed or public.
// - "Submit for review": the product goes to Pending and stays hidden until
//   an admin approves it (also after every edit). A super admin in member
//   mode publishes directly (server rule).
// - Saving a draft of changes to a product already submitted or live keeps
//   the field changes private (draft_data) and leaves the live version
//   online; photo and brochure changes are applied when submitting.
const STEPS = ['category', 'photos', 'details', 'price'];
const MAIN_UNITS = ['tonne', 'kg', 'container_20', 'container_40', 'piece', 'litre'];
const MAIN_INCOTERMS = ['EXW', 'FOB', 'CFR', 'CIF', 'DAP'];
const CURRENCIES = ['USD', 'CAD', 'EUR'];
const PAYMENT = ['tt', 'lc', 'cad', 'escrow'];
const CERTS = ['ISO 9001', 'ISO 14001', 'ISO 22000', 'HACCP', 'Halal', 'Kosher', 'Organic', 'CE', 'SGS', 'Mill test certificate', 'FDA', 'GMP', 'BRCGS', 'REACH', 'RoHS', 'CSA', 'UL'];
const AUTOSAVE_MS = 30 * 1000;
const MAX_PDF = 10 * 1024 * 1024;

const blank = {
  categoryId: '', title: '', descriptionHtml: '', videoUrl: '',
  specValues: {}, certifications: [], modelNo: '', specs: [],
  moq: '', moqUnit: '', price: '', priceMax: '', priceUnit: '', currency: 'USD', priceNegotiable: false, priceOnRequest: false,
  incoterm: '', paymentTerms: [], productionCapacity: '', deliveryTime: '', packaging: '', loadingPort: '', originCountry: '',
  // Kept from the earlier form ("More details"), sent back unchanged.
  grade: '', brand: '', trademark: '', sellerCode: '', hsCode: '', standards: '', applications: '',
  sampleAvailable: '', sampleCostBy: '', quantity: '', unit: '', advantages: [], featured: false, productTypeId: '',
};
const TEXT_KEYS = ['title', 'descriptionHtml', 'videoUrl', 'modelNo', 'moq', 'moqUnit', 'price', 'priceMax', 'priceUnit', 'incoterm', 'productionCapacity',
  'deliveryTime', 'packaging', 'loadingPort', 'originCountry', 'grade', 'brand', 'trademark', 'sellerCode', 'hsCode', 'standards', 'applications', 'sampleCostBy', 'quantity', 'unit', 'productTypeId'];

const s = (v) => (v === null || v === undefined ? '' : String(v));
function toForm(src) {
  const specValues = {};
  for (const x of src.catalogSpecs || []) (specValues[x.groupId] ||= []).push(x.value);
  return {
    ...blank,
    ...Object.fromEntries(TEXT_KEYS.map((k) => [k, s(src[k])])),
    categoryId: s(src.categoryId ?? src.category?.id),
    currency: CURRENCIES.includes(src.currency) ? src.currency : 'USD',
    priceNegotiable: !!src.priceNegotiable, priceOnRequest: !!src.priceOnRequest, featured: !!src.featured,
    paymentTerms: [...(src.paymentTerms || [])], certifications: [...(src.certifications || [])],
    sampleAvailable: src.sampleAvailable === true ? 'yes' : src.sampleAvailable === false ? 'no' : '',
    specs: (src.specs || []).map((x) => ({ label: x.label, value: x.value })),
    advantages: [...(src.advantages || [])],
    specValues,
  };
}
const optNum = (v) => (String(v).trim() === '' ? null : Number(String(v).replace(',', '.')));
function toBody(f, draft, fields) {
  const price = optNum(f.price);
  return {
    ...f, draft,
    categoryId: f.categoryId ? Number(f.categoryId) : null,
    price, priceMax: optNum(f.priceMax), priceOnRequest: price === null,
    priceUnit: f.priceUnit || f.moqUnit || '',
    moq: optNum(f.moq), quantity: optNum(f.quantity),
    sampleAvailable: f.sampleAvailable === 'yes' ? true : f.sampleAvailable === 'no' ? false : null,
    sampleCostBy: f.sampleAvailable === 'yes' ? f.sampleCostBy : '',
    specs: f.specs.filter((x) => x.label.trim() || x.value.trim()),
    advantages: f.advantages.filter((a) => a.trim()),
    catalogSpecs: specsToBody(fields, f.specValues),
    specValues: undefined,
  };
}
const badNum = (v) => String(v).trim() !== '' && (Number.isNaN(Number(String(v).replace(',', '.'))) || Number(String(v).replace(',', '.')) < 0);

export default function ListingForm() {
  const { id: routeId } = useParams();
  const [search] = useSearchParams();
  const fromId = !routeId ? search.get('from') : null; // "Duplicate" in My products
  const { t, lang, errorText } = useI18n();
  const { user } = useAuth();
  const tree = useCategoryTree();
  const countries = useMemo(() => countryOptions(lang), [lang]);

  const [id, setId] = useState(routeId || null);
  usePageTitle(id ? 'listingForm.editTitle' : 'listingForm.newTitle');
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(blank);
  const [photos, setPhotos] = useState([]);
  const [brochure, setBrochure] = useState(null); // saved { url, name }
  const [brochureFile, setBrochureFile] = useState(null); // picked, not uploaded yet
  const [brochureRemoved, setBrochureRemoved] = useState(false);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(!!routeId || !!fromId);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [errs, setErrs] = useState({});
  const [countryText, setCountryText] = useState('');
  const [recent, setRecent] = useState([]);
  const [template, setTemplate] = useState({ categoryId: null, fields: [] });
  const [crop, setCrop] = useState(null); // photo index being cropped
  const [done, setDone] = useState(null); // submitted product
  const [certText, setCertText] = useState('');
  const [savedSnap, setSavedSnap] = useState(null);
  const busyRef = useRef(false);
  const typeMapped = useRef(false);
  const rebase = useRef(null); // the loaded product was adjusted, not edited: not "unsaved changes"

  // ── Load ──────────────────────────────────────────────────────────────
  useEffect(() => { api('/my/listings/recent-categories').then(setRecent).catch(() => {}); }, []);
  // Duplicate: a new product with everything copied except the photos,
  // the name and the brochure.
  useEffect(() => {
    if (routeId || !fromId) return;
    api(`/my/listings/${fromId}`).then((l) => {
      const src = l.status !== 'draft' && l.draftData ? { ...l.draftData, catalogSpecs: l.draftData.catalogSpecs || l.catalogSpecs } : l;
      setForm({ ...toForm(src), title: '' });
      setStep(1);
    }).catch((err) => setError(errorText(err))).finally(() => setLoading(false));
  }, [fromId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!routeId) return;
    api(`/my/listings/${routeId}`).then((l) => {
      setOriginal(l);
      // Saved draft changes to a submitted/live product take precedence.
      const src = l.status !== 'draft' && l.draftData ? { ...l.draftData, catalogSpecs: l.draftData.catalogSpecs || l.catalogSpecs } : l;
      setForm(toForm(src));
      setPhotos(l.photos);
      setBrochure(l.brochure);
      setStep(1);
    }).catch((err) => setError(errorText(err))).finally(() => setLoading(false));
  }, [routeId, errorText]);
  useEffect(() => { setCountryText(countryName(form.originCountry, lang)); }, [form.originCountry, lang]);

  // A product filed before the tree under a department, with a product
  // type: open it on that product type's item.
  useEffect(() => {
    if (typeMapped.current || !tree.list.length || !form.categoryId || !form.productTypeId) return;
    typeMapped.current = true;
    const cat = tree.get(form.categoryId);
    if (!cat || cat.contentRef === `type:${form.productTypeId}`) return;
    const hit = tree.below(cat.id).find((n) => n.contentRef === `type:${form.productTypeId}`);
    if (hit) { rebase.current = String(hit.id); setForm((f) => ({ ...f, categoryId: String(hit.id) })); }
  }, [tree, form.categoryId, form.productTypeId]);

  // Spec template of the chosen category.
  const loadTemplate = useCallback(async (categoryId) => {
    if (!categoryId) return [];
    if (String(template.categoryId) === String(categoryId)) return template.fields;
    const fields = await api(`/categories/${categoryId}/fields`);
    setTemplate({ categoryId: String(categoryId), fields });
    return fields;
  }, [template]);
  useEffect(() => { if (form.categoryId) loadTemplate(form.categoryId).catch(() => {}); }, [form.categoryId]); // eslint-disable-line react-hooks/exhaustive-deps
  const fields = String(template.categoryId) === String(form.categoryId) ? template.fields : [];

  const snapshot = JSON.stringify({ form, p: photos.map((p) => p.id ?? p.tempId), b: brochureFile?.name || null, r: brochureRemoved });
  useEffect(() => { if (savedSnap === null && !loading) setSavedSnap(snapshot); }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (rebase.current && rebase.current === form.categoryId) { rebase.current = null; setSavedSnap(snapshot); } });
  const dirty = savedSnap !== null && snapshot !== savedSnap;

  const set = (k) => (e) => { const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value; setForm((f) => ({ ...f, [k]: v })); setErrs((x) => ({ ...x, [k]: '' })); };
  const setVal = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrs((x) => ({ ...x, [k]: '' })); };
  const toggle = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));
  const isDraft = !original || original.status === 'draft';
  const canPost = user.emailVerified && user.profileComplete;

  // ── Validation (inline, per step) ─────────────────────────────────────
  function check(n) {
    const e = {};
    if (n === 0 && !form.categoryId) e.categoryId = t('wizard.err.category');
    if (n === 1) {
      if (!photos.length) e.photos = t('wizard.err.photo');
      if (form.title.trim().length < 3) e.title = t('errors.title_too_short');
      if (form.videoUrl.trim() && !/^https:\/\/[^\s/]+\.[^\s]+$/i.test(form.videoUrl.trim())) e.videoUrl = t('errors.invalid_video_url');
    }
    if (n === 2) Object.assign(e, checkSpecs(fields, form.specValues, t, lang), form.specs.some((x) => !!x.label.trim() !== !!x.value.trim()) ? { specs: t('errors.spec_incomplete') } : {});
    if (n === 3) {
      if (String(form.moq).trim() === '' || badNum(form.moq) || Number(String(form.moq).replace(',', '.')) <= 0) e.moq = t('wizard.err.moq');
      if (!form.moqUnit) e.moqUnit = t('wizard.err.unit');
      if (badNum(form.price)) e.price = t('errors.invalid_price');
      if (badNum(form.priceMax)) e.priceMax = t('errors.invalid_price');
      else if (String(form.priceMax).trim() && String(form.price).trim() && Number(form.priceMax) < Number(form.price)) e.priceMax = t('errors.price_range');
      if (countryText && !form.originCountry) e.originCountry = t('listingForm.countryPick');
    }
    return e;
  }
  const goTo = (n) => { setStep(n); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  function go(next) {
    if (next > step) {
      for (let n = step; n < next; n++) {
        const e = check(n);
        if (Object.keys(e).length) { setErrs(e); goTo(n); return; }
      }
    }
    setErrs({}); setError(''); setToast('');
    goTo(next);
  }

  // ── Saving ────────────────────────────────────────────────────────────
  async function syncPhotos(listing) {
    const keep = new Set(photos.filter((p) => p.id).map((p) => p.id));
    for (const p of listing.photos) {
      if (!keep.has(p.id)) listing = await api(`/my/listings/${listing.id}/photos/${p.id}`, { method: 'DELETE' });
    }
    const added = photos.filter((p) => p.file && !p.savedId);
    if (added.length) {
      const fd = new FormData();
      for (const [i, p] of added.entries()) {
        setProgress(t('listingForm.uploading', { n: i + 1, total: added.length }));
        const blob = await resizeImage(p.file);
        fd.append('photos', blob, `photo-${i}.${blob.type === 'image/webp' ? 'webp' : 'jpg'}`);
      }
      const before = new Set(listing.photos.map((p) => p.id));
      listing = await api(`/my/listings/${listing.id}/photos`, { method: 'POST', form: fd });
      const ids = listing.photos.filter((p) => !before.has(p.id)).map((p) => p.id);
      added.forEach((p, i) => { p.savedId = ids[i]; });
    }
    const wanted = photos.map((p) => p.id ?? p.savedId).filter(Boolean);
    if (wanted.join() !== listing.photos.map((p) => p.id).join()) {
      listing = await api(`/my/listings/${listing.id}/photos/order`, { method: 'PUT', body: { ids: wanted } });
    }
    return listing;
  }
  async function syncBrochure(listing) {
    if (brochureFile) {
      const fd = new FormData();
      fd.append('file', brochureFile, brochureFile.name);
      listing = await api(`/my/listings/${listing.id}/brochure`, { method: 'POST', form: fd });
    } else if (brochureRemoved && listing.brochure) {
      listing = await api(`/my/listings/${listing.id}/brochure`, { method: 'DELETE' });
    }
    return listing;
  }
  function serverError(err) {
    if (err.code === 'spec_required') { setErrs({ [err.data?.key]: t('wizard.required', { field: lang === 'en' ? err.data?.fieldEn : err.data?.field }) }); goTo(2); return; }
    const at = { photo_required: [1, 'photos'], title_too_short: [1, 'title'], invalid_video_url: [1, 'videoUrl'], invalid_category: [0, 'categoryId'], category_level: [0, 'categoryId'], moq_required: [3, 'moq'], moq_unit_required: [3, 'moqUnit'], invalid_spec: [2], invalid_spec_number: [2] }[err.code];
    if (at) { goTo(at[0]); if (at[1]) setErrs({ [at[1]]: errorText(err) }); }
    setError(errorText(err));
  }

  async function save(draft, { auto = false } = {}) {
    if (busyRef.current) return;
    setError(''); if (!auto) setToast('');
    if (!draft) {
      for (let n = 0; n < STEPS.length; n++) {
        const e = check(n);
        if (Object.keys(e).length) { setErrs(e); goTo(n); return; }
      }
    }
    busyRef.current = true; setBusy(true);
    const snap = snapshot;
    try {
      const flds = await loadTemplate(form.categoryId);
      const body = toBody(form, true, flds);
      // A new product is first saved as a draft, so photos can be added
      // before it's submitted. Submitting a product that is already
      // submitted or live sends its fields once, at the end.
      let listing;
      if (!id) listing = await api('/my/listings', { method: 'POST', body });
      else if (draft || isDraft) listing = await api(`/my/listings/${id}`, { method: 'PATCH', body });
      else listing = await api(`/my/listings/${id}`);
      if (!id) {
        setId(String(listing.id));
        window.history.replaceState(window.history.state, '', `/account/listings/${listing.id}/edit`);
      }
      // Photos / brochure: saved with a draft product, or when submitting.
      if (!draft || listing.status === 'draft') { listing = await syncPhotos(listing); listing = await syncBrochure(listing); }
      if (!draft) listing = await api(`/my/listings/${listing.id}`, { method: 'PATCH', body: { ...body, draft: false } });
      setProgress('');
      setOriginal(listing);
      if (!draft || listing.status === 'draft') {
        setPhotos(listing.photos); setBrochure(listing.brochure); setBrochureFile(null); setBrochureRemoved(false);
      }
      setSavedSnap(draft && listing.status !== 'draft' ? snap : JSON.stringify({ form, p: listing.photos.map((p) => p.id), b: null, r: false }));
      if (!draft) { setDone(listing); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
      const time = new Date().toLocaleTimeString(lang === 'en' ? 'en-CA' : 'fr-CA', { hour: '2-digit', minute: '2-digit' });
      setToast(auto ? t('wizard.autosaved', { time }) : listing.status === 'draft' ? t('listingForm.draftSaved') : t('listingForm.draftChangesSaved'));
    } catch (err) {
      setProgress('');
      if (auto) setToast(''); else serverError(err);
    } finally {
      busyRef.current = false; setBusy(false);
    }
  }

  // Autosave every 30 s while there are unsaved changes.
  const saveRef = useRef(save);
  saveRef.current = save;
  const autoOk = dirty && canPost && !done && !!(form.categoryId || form.title.trim());
  useEffect(() => {
    if (!autoOk) return undefined;
    const timer = setInterval(() => { saveRef.current(true, { auto: true }); }, AUTOSAVE_MS);
    return () => clearInterval(timer);
  }, [autoOk]);
  useEffect(() => {
    if (!dirty || done) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, done]);

  // After submitting: start again, or copy this product (except photos,
  // name and brochure).
  function restart(similar) {
    const next = similar ? { ...form, title: '' } : { ...blank, specValues: {} };
    setForm(next); setPhotos([]); setBrochure(null); setBrochureFile(null); setBrochureRemoved(false);
    setId(null); setOriginal(null); setDone(null); setErrs({}); setError(''); setToast('');
    setSavedSnap(JSON.stringify({ form: next, p: [], b: null, r: false }));
    typeMapped.current = true;
    window.history.replaceState(window.history.state, '', '/account/listings/new');
    api('/my/listings/recent-categories').then(setRecent).catch(() => {});
    goTo(similar ? 1 : 0);
  }

  // ── Render ────────────────────────────────────────────────────────────
  // Company users: their role first (own message), then the company's plan.
  const noAdd = !routeId && !allowed(user, 'products.create');
  if (noAdd || !user.isSeller) {
    return (
      <div className="container page page-narrow">
        <p><Link to="/account/products" className="back-link">← {t('myListings.title')}</Link></p>
        {noAdd ? <NoPermissionCard /> : <UpgradeCard reason="sell" />}
      </div>
    );
  }
  if (loading) return <div className="page-loading">{t('common.loading')}</div>;

  if (done) {
    const live = done.status === 'approved';
    // Sent by a team member to the company's owner / an admin (migration 031).
    if (done.awaitingTeam) {
      return (
        <div className="container page wizard-page">
          <div className="card wz-done">
            <span className="wz-done-icon" aria-hidden="true">✓</span>
            <h1>{t('wizard.doneTeamTitle')}</h1>
            <p className="muted">{t(user?.pmeProducts ? 'wizard.doneTeamTextPme' : 'wizard.doneTeamText')}</p>
            <p className="wz-done-name"><strong>{done.title}</strong></p>
            <div className="wz-done-actions"><button type="button" className="btn btn-ghost" onClick={() => restart(false)}>{t('wizard.addAnother')}</button></div>
            <Link to="/account/products">{t('wizard.seeProducts')} →</Link>
          </div>
        </div>
      );
    }
    return (
      <div className="container page wizard-page">
        <div className="card wz-done">
          <span className="wz-done-icon" aria-hidden="true">✓</span>
          <p className="eyebrow">{live ? t('wizard.doneLiveEyebrow') : t('wizard.doneEyebrow')}</p>
          <h1>{live ? t('wizard.doneLiveTitle') : t('wizard.doneTitle')}</h1>
          <p className="muted">{live ? t('wizard.doneLiveText') : t('wizard.doneText')}</p>
          <p className="wz-done-name"><strong>{done.title}</strong></p>
          <div className="wz-done-actions">
            <button type="button" className="btn btn-ghost" onClick={() => restart(false)}>{t('wizard.addAnother')}</button>
            <button type="button" className="btn btn-primary" onClick={() => restart(true)}>{t('wizard.addSimilar')}</button>
          </div>
          <p className="small muted">{t('wizard.similarHint')}</p>
          <Link to="/account/products">{t('wizard.seeProducts')} →</Link>
        </div>
      </div>
    );
  }

  const err = (k) => (errs[k] ? <p className="field-error" role="alert">{errs[k]}</p> : null);
  const inv = (k) => (errs[k] ? 'is-invalid' : undefined);
  const req = <span className="req" aria-hidden="true"> *</span>;
  const unitName = (u) => (u === 'tonne' ? t('wizard.unitMT') : t(`units.${u}`));
  const chosen = form.categoryId ? tree.get(form.categoryId) : null;
  const priceUnit = form.priceUnit || form.moqUnit;
  const pdfPicked = (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) { setErrs((x) => ({ ...x, brochure: t('errors.invalid_pdf') })); return; }
    if (file.size > MAX_PDF) { setErrs((x) => ({ ...x, brochure: t('errors.file_too_large') })); return; }
    setErrs((x) => ({ ...x, brochure: '' })); setBrochureFile(file); setBrochureRemoved(false);
  };
  const addCert = () => {
    const c = certText.trim().replace(/,/g, ' ').slice(0, 60);
    if (c && !form.certifications.includes(c)) setVal('certifications', [...form.certifications, c]);
    setCertText('');
  };

  return (
    <div className="container page wizard-page">
      <p><Link to="/account/products" className="back-link">← {t('myListings.title')}</Link></p>
      <div className="wizard-head">
        <h1>{id ? t('listingForm.editTitle') : t('listingForm.newTitle')}</h1>
        {original && <span className={`status status-${original.status}`}>{t(`myListings.status.${original.status}`)}</span>}
        <TutorialSlot place={['ws.products.add', `ws.products.add.s${step + 1}`]} className="wizard-watch" />
      </div>
      {!user.emailVerified && <div className="callout callout-warning"><p>{t('account.verifyToPost')}</p></div>}
      {user.emailVerified && !user.profileComplete && <div className="callout callout-warning"><p>{t('account.completeToPost')} <Link to="/account/complete-profile">{t('account.completeProfileLink')}</Link></p></div>}
      {original?.status === 'rejected' && original.rejectionReason && <div className="callout callout-warning"><p>{t('listingForm.rejectedNote', { reason: original.rejectionReason })}</p></div>}
      {original && original.status !== 'draft' && original.draftData && <div className="callout callout-info"><p>{t('listingForm.editingDraftChanges')}</p></div>}

      <nav className="wz-steps" aria-label={t('listingForm.progress')}>
        <ol>
          {STEPS.map((k, i) => (
            <li key={k} className={i === step ? 'on' : i < step ? 'done' : ''}>
              <button type="button" onClick={() => go(i)} aria-current={i === step ? 'step' : undefined} disabled={busy}>
                <span className="wz-num">{i < step ? '✓' : i + 1}</span>
                <span className="wz-label">{t(`wizard.steps.${k}`)}</span>
              </button>
            </li>
          ))}
        </ol>
        <p className="wz-now">{t('listingForm.stepOf', { n: step + 1, total: STEPS.length })} — <strong>{t(`wizard.steps.${STEPS[step]}`)}</strong></p>
      </nav>

      <form className="card form wizard-card" onSubmit={(e) => { e.preventDefault(); if (step < STEPS.length - 1) go(step + 1); else save(false); }} noValidate>
        {step > 0 && chosen && (
          <div className="wz-chosen wz-chosen-bar">
            <div><span className="muted small">{t('wizard.chosenCategory')}</span><strong>{tree.pathText(chosen.id, lang)}</strong></div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => goTo(0)}>{t('wizard.change')}</button>
          </div>
        )}

        {step === 0 && (
          <>
            <h2 className="card-title">{t('wizard.titles.category')}</h2>
            <CategoryStep tree={tree} value={form.categoryId} recent={recent} error={errs.categoryId}
              onPick={(cid, opt) => { setVal('categoryId', String(cid)); setVal('forceCategory', !!opt?.force); setErrs({}); goTo(1); }} />
          </>
        )}

        {step === 1 && (
          <>
            <h2 className="card-title">{t('wizard.titles.photos')}</h2>
            <div className="field">
              <span className="field-label">{t('listingForm.photos')}{req}</span>
              <p className="field-hint">{t('wizard.photosHint')}</p>
              {!isDraft && <p className="callout callout-info small">{t('listingForm.photosOnSubmit')}</p>}
              <PhotoUploader items={photos} onChange={(p) => { setPhotos(p); setErrs((x) => ({ ...x, photos: '' })); }} disabled={busy} onCrop={setCrop} />
              {err('photos')}
            </div>
            <Field label={<>{t('wizard.productName')}{req}</>} htmlFor="lf-title">
              <input id="lf-title" className={inv('title')} value={form.title} onChange={set('title')} maxLength={160} placeholder={t('wizard.namePlaceholder')} />
              {err('title')}
            </Field>
            <Field label={t('wizard.shortDescription')} optional htmlFor="lf-desc">
              <RichTextEditor id="lf-desc" value={form.descriptionHtml} onChange={(v) => setVal('descriptionHtml', v)} placeholder={t('wizard.descriptionPlaceholder')} />
            </Field>
            <div className="grid-form">
              <Field label={t('wizard.video')} optional hint={t('wizard.videoHint')} htmlFor="lf-video">
                <input id="lf-video" type="url" inputMode="url" className={inv('videoUrl')} value={form.videoUrl} onChange={set('videoUrl')} maxLength={255} placeholder="https://youtu.be/…" />
                {err('videoUrl')}
              </Field>
              <div className="field">
                <span className="field-label">{t('wizard.brochure')} <span className="field-optional">({t('common.optional')})</span></span>
                {(brochureFile || (brochure && !brochureRemoved)) ? (
                  <div className="wz-file">
                    <span aria-hidden="true">📄</span>
                    {brochureFile ? <span>{brochureFile.name}</span> : <a href={brochure.url} target="_blank" rel="noreferrer">{brochure.name}</a>}
                    <button type="button" className="btn btn-link btn-sm" onClick={() => { setBrochureFile(null); setBrochureRemoved(true); }}>{t('wizard.removeFile')}</button>
                  </div>
                ) : (
                  <label className="btn btn-ghost btn-sm wz-file-pick">
                    {t('wizard.addPdf')}
                    <input type="file" accept="application/pdf,.pdf" className="sr-only" onChange={(e) => { pdfPicked(e.target.files[0]); e.target.value = ''; }} />
                  </label>
                )}
                <p className="field-hint">{t('wizard.brochureHint')}</p>
                {err('brochure')}
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h2 className="card-title">{t('wizard.titles.details')}</h2>
            {fields.length > 0
              ? <p className="muted small">{t('wizard.detailsLead', { name: chosen ? tree.pathText(chosen.id, lang).split(' › ').pop() : '' })}</p>
              : <p className="muted small">{t('wizard.noTemplate')}</p>}
            <SpecFields fields={fields} values={form.specValues} errors={errs}
              onChange={(v) => { setForm((f) => ({ ...f, specValues: v })); setErrs({}); }} />

            <div className="field" role="group" aria-labelledby="lf-cert-l">
              <span className="field-label" id="lf-cert-l">{t('wizard.certifications')} <span className="field-optional">({t('common.optional')})</span></span>
              <div className="chip-choice">
                {[...CERTS, ...form.certifications.filter((c) => !CERTS.includes(c))].map((c) => {
                  const on = form.certifications.includes(c);
                  return <button key={c} type="button" className={`chip-toggle${on ? ' on' : ''}`} aria-pressed={on} onClick={() => toggle('certifications', c)}>{c === 'SGS' ? t('wizard.sgs') : c === 'Mill test certificate' ? t('wizard.mtc') : c}</button>;
                })}
              </div>
              <div className="wz-inline-add">
                <input aria-label={t('wizard.otherCert')} placeholder={t('wizard.otherCert')} value={certText} maxLength={60} onChange={(e) => setCertText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCert(); } }} />
                <button type="button" className="btn btn-ghost btn-sm" onClick={addCert} disabled={!certText.trim()}>{t('wizard.add')}</button>
              </div>
            </div>

            <Field label={t('wizard.modelNo')} optional htmlFor="lf-model">
              <input id="lf-model" value={form.modelNo} onChange={set('modelNo')} maxLength={120} placeholder={t('wizard.modelPlaceholder')} />
            </Field>

            <div className="field">
              <span className="field-label">{t('wizard.customDetails')} <span className="field-optional">({t('common.optional')})</span></span>
              {form.specs.map((sp, i) => (
                <div key={i} className="spec-row">
                  <input aria-label={t('listingForm.specLabel', { n: i + 1 })} placeholder={t('wizard.attribute')} value={sp.label} maxLength={60}
                    onChange={(e) => setVal('specs', form.specs.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
                  <input aria-label={t('listingForm.specValue', { n: i + 1 })} placeholder={t('wizard.value')} value={sp.value} maxLength={120}
                    onChange={(e) => setVal('specs', form.specs.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
                  <button type="button" className="btn btn-link btn-sm" onClick={() => setVal('specs', form.specs.filter((_, j) => j !== i))} aria-label={t('listingForm.removeRow')}>×</button>
                </div>
              ))}
              {form.specs.length < MAX_SPECS && <button type="button" className="btn btn-ghost btn-sm wz-add-detail" onClick={() => setVal('specs', [...form.specs, { label: '', value: '' }])}>+ {t('wizard.addCustom')}</button>}
              {err('specs')}
            </div>

            <details className="wz-more">
              <summary>{t('wizard.moreDetails')}</summary>
              <div className="grid-form grid-3">
                <Field label={t('product.fields.brand')} optional htmlFor="lf-brand"><input id="lf-brand" value={form.brand} onChange={set('brand')} maxLength={120} /></Field>
                <Field label={t('product.fields.grade')} optional htmlFor="lf-grade"><input id="lf-grade" value={form.grade} onChange={set('grade')} maxLength={120} /></Field>
                <Field label={t('product.fields.hsCode')} optional htmlFor="lf-hs"><input id="lf-hs" value={form.hsCode} onChange={set('hsCode')} maxLength={20} inputMode="decimal" placeholder="1006.30" /></Field>
                <Field label={t('product.fields.standards')} optional htmlFor="lf-std"><input id="lf-std" value={form.standards} onChange={set('standards')} maxLength={255} placeholder="ASTM, EN, BIS…" /></Field>
                <Field label={<>{t('product.fields.sellerCode')} <span className="private-tag">🔒 {t('company.private')}</span></>} optional htmlFor="lf-code"><input id="lf-code" value={form.sellerCode} onChange={set('sellerCode')} maxLength={60} /></Field>
                <div className="field">
                  <span className="field-label">{t('product.fields.sample')}</span>
                  <div className="chip-choice">
                    {['yes', 'no'].map((v) => <button key={v} type="button" className={`chip-toggle${form.sampleAvailable === v ? ' on' : ''}`} aria-pressed={form.sampleAvailable === v} onClick={() => setVal('sampleAvailable', form.sampleAvailable === v ? '' : v)}>{t(`common.${v}`)}</button>)}
                  </div>
                </div>
              </div>
              <Field label={t('product.fields.applications')} optional htmlFor="lf-use"><textarea id="lf-use" rows={2} value={form.applications} onChange={set('applications')} maxLength={2000} /></Field>
            </details>
          </>
        )}

        {step === 3 && (
          <>
            <h2 className="card-title">{t('wizard.titles.price')}</h2>
            <div className="field" role="group" aria-labelledby="lf-moq-l">
              <span className="field-label" id="lf-moq-l">{t('product.fields.moq')}{req}</span>
              <div className="wz-moq">
                <input id="lf-moq" aria-label={t('product.fields.moq')} className={inv('moq')} inputMode="decimal" value={form.moq} onChange={set('moq')} placeholder="25" />
                <div className="chip-choice">
                  {MAIN_UNITS.map((u) => <button key={u} type="button" className={`chip-toggle${form.moqUnit === u ? ' on' : ''}`} aria-pressed={form.moqUnit === u} onClick={() => setVal('moqUnit', u)}>{unitName(u)}</button>)}
                  <select aria-label={t('wizard.moreUnits')} className={`wz-more-select${form.moqUnit && !MAIN_UNITS.includes(form.moqUnit) ? ' on' : ''}`} value={MAIN_UNITS.includes(form.moqUnit) ? '' : form.moqUnit} onChange={(e) => setVal('moqUnit', e.target.value)}>
                    <option value="">{t('wizard.moreUnits')}</option>
                    {UNITS.filter((u) => !MAIN_UNITS.includes(u)).map((u) => <option key={u} value={u}>{t(`units.${u}`)}</option>)}
                  </select>
                </div>
              </div>
              {err('moq')}{err('moqUnit')}
            </div>

            <fieldset className="price-box">
              <legend>{t('wizard.priceLegend')} <span className="field-optional">({t('common.optional')})</span></legend>
              <div className="chip-choice" role="radiogroup" aria-label={t('wizard.currency')}>
                {CURRENCIES.map((c) => <button key={c} type="button" role="radio" aria-checked={form.currency === c} className={`chip-toggle${form.currency === c ? ' on' : ''}`} onClick={() => setVal('currency', c)}>{c}</button>)}
              </div>
              <div className="grid-form grid-3">
                <Field label={t('wizard.priceMin', { cur: form.currency })} htmlFor="lf-price">
                  <input id="lf-price" className={inv('price')} inputMode="decimal" value={form.price} onChange={set('price')} placeholder="0.00" />
                  {err('price')}
                </Field>
                <Field label={t('wizard.priceMax', { cur: form.currency })} optional htmlFor="lf-pmax">
                  <input id="lf-pmax" className={inv('priceMax')} inputMode="decimal" value={form.priceMax} onChange={set('priceMax')} placeholder="0.00" />
                  {err('priceMax')}
                </Field>
                <Field label={t('wizard.perUnit')} htmlFor="lf-punit">
                  <select id="lf-punit" value={form.priceUnit} onChange={set('priceUnit')}>
                    <option value="">{form.moqUnit ? t('wizard.sameUnit', { unit: t(`units.${form.moqUnit}`) }) : t('listingForm.perUnitNone')}</option>
                    {UNITS.map((u) => <option key={u} value={u}>{unitName(u)}</option>)}
                  </select>
                </Field>
              </div>
              <label className="checkbox"><input type="checkbox" checked={form.priceNegotiable} onChange={set('priceNegotiable')} /><span>{t('wizard.negotiable')}</span></label>
              <p className="field-hint">
                {t('listingForm.pricePreview')} <strong>{String(form.price).trim() && !badNum(form.price)
                  ? formatPrice({ price: Number(String(form.price).replace(',', '.')), priceMax: String(form.priceMax).trim() && !badNum(form.priceMax) ? Number(String(form.priceMax).replace(',', '.')) : null, priceUnit, currency: form.currency }, lang, t)
                  : t('marketplace.priceOnRequest')}</strong>{form.priceNegotiable && ` · ${t('wizard.negotiableShort')}`}
              </p>
            </fieldset>

            <div className="field" role="group" aria-labelledby="lf-inco-l">
              <span className="field-label" id="lf-inco-l">{t('product.fields.incoterm')} <span className="field-optional">({t('common.optional')})</span></span>
              <div className="chip-choice">
                {MAIN_INCOTERMS.map((c) => <button key={c} type="button" title={incotermLabel(c, t)} className={`chip-toggle${form.incoterm === c ? ' on' : ''}`} aria-pressed={form.incoterm === c} onClick={() => setVal('incoterm', form.incoterm === c ? '' : c)}>{c}</button>)}
                <select aria-label={t('wizard.moreIncoterms')} className={`wz-more-select${form.incoterm && !MAIN_INCOTERMS.includes(form.incoterm) ? ' on' : ''}`} value={MAIN_INCOTERMS.includes(form.incoterm) ? '' : form.incoterm} onChange={(e) => setVal('incoterm', e.target.value)}>
                  <option value="">{t('wizard.moreIncoterms')}</option>
                  {INCOTERMS.filter((c) => !MAIN_INCOTERMS.includes(c)).map((c) => <option key={c} value={c}>{incotermLabel(c, t)}</option>)}
                </select>
              </div>
              {form.incoterm && <p className="field-hint">{incotermLabel(form.incoterm, t)}</p>}
            </div>

            <div className="field" role="group" aria-labelledby="lf-pay-l">
              <span className="field-label" id="lf-pay-l">{t('wizard.payment')} <span className="field-optional">({t('common.optional')})</span></span>
              <div className="chip-choice">
                {PAYMENT.map((c) => <button key={c} type="button" className={`chip-toggle${form.paymentTerms.includes(c) ? ' on' : ''}`} aria-pressed={form.paymentTerms.includes(c)} title={t(`payment.${c}Long`)} onClick={() => toggle('paymentTerms', c)}>{t(`payment.${c}`)}</button>)}
              </div>
            </div>

            <div className="grid-form">
              <Field label={t('wizard.supply')} optional htmlFor="lf-cap"><input id="lf-cap" value={form.productionCapacity} onChange={set('productionCapacity')} maxLength={120} placeholder={t('wizard.supplyPlaceholder')} /></Field>
              <Field label={t('product.fields.deliveryTime')} optional htmlFor="lf-deliv"><input id="lf-deliv" value={form.deliveryTime} onChange={set('deliveryTime')} maxLength={60} placeholder={t('listingForm.deliveryPlaceholder')} /></Field>
            </div>
            <Field label={t('product.fields.packaging')} optional htmlFor="lf-pack"><input id="lf-pack" value={form.packaging} onChange={set('packaging')} maxLength={500} placeholder={t('wizard.packagingPlaceholder')} /></Field>
            <div className="grid-form">
              <Field label={t('product.fields.originCountry')} optional htmlFor="lf-origin">
                <input id="lf-origin" className={inv('originCountry')} list="lf-countries" value={countryText} autoComplete="off" placeholder={t('listingForm.countryPlaceholder')}
                  onChange={(e) => {
                    const text = e.target.value;
                    setCountryText(text);
                    const hit = countries.find((c) => c.name.toLowerCase() === text.trim().toLowerCase());
                    setVal('originCountry', hit ? hit.code : '');
                  }} />
                <datalist id="lf-countries">{countries.map((c) => <option key={c.code} value={c.name} />)}</datalist>
                {err('originCountry')}
              </Field>
              <Field label={t('wizard.port')} optional htmlFor="lf-port"><input id="lf-port" value={form.loadingPort} onChange={set('loadingPort')} maxLength={120} placeholder={t('listingForm.portPlaceholder')} /></Field>
            </div>
            <p className="field-hint">{t('listingForm.pendingNote')}</p>
          </>
        )}

        <Alert>{error}</Alert>
        <div className="wz-actions">
          <div className="wz-actions-status" aria-live="polite">
            {progress ? <span className="muted small">{progress}</span> : toast ? <span className="wz-saved small">✓ {toast}</span> : dirty ? <span className="muted small">{t('wizard.unsaved')}</span> : null}
          </div>
          <div className="wz-actions-row">
            {step > 0 ? <button type="button" className="btn btn-ghost" onClick={() => go(step - 1)} disabled={busy}>← {t('listingForm.back')}</button> : <span />}
            <div className="wizard-actions-right">
              <button type="button" className="btn btn-ghost" onClick={() => save(true)} disabled={busy || !canPost}>{busy ? t('common.saving') : t('listingForm.saveDraft')}</button>
              {step < STEPS.length - 1
                ? <button type="submit" className="btn btn-primary" disabled={busy}>{t('listingForm.next')} →</button>
                : <button type="submit" className="btn btn-primary" disabled={busy || !canPost}>{busy ? t('common.saving') : t('listingForm.submit')}</button>}
            </div>
          </div>
        </div>
      </form>

      {crop !== null && photos[crop] && (
        <CropModal source={photos[crop].file || photos[crop].url} returnBlob outWidth={1600} aspect={4 / 3}
          aspects={[{ value: 4 / 3, label: '4:3' }, { value: 1, label: '1:1' }, { value: 3 / 4, label: '3:4' }, { value: 16 / 9, label: '16:9' }]}
          onDone={(r) => {
            const i = crop;
            setCrop(null);
            if (!r?.blob) return;
            const file = new File([r.blob], `photo.${r.blob.type === 'image/webp' ? 'webp' : 'jpg'}`, { type: r.blob.type });
            setPhotos((list) => list.map((p, j) => (j === i ? { tempId: `crop-${Date.now()}`, file, url: URL.createObjectURL(file) } : p)));
          }} />
      )}
    </div>
  );
}

